import { CSS } from '@dnd-kit/utilities';
import { ChakraV3, DndKitCore, DndKitSortable } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type Table as RTable,
  useReactTable,
} from '@tanstack/react-table';
import React from 'react';
import { DragHandle } from './DragHandle';
import { Empty } from './Empty';
import { Pagination, type PaginationOptions, type TimestampPaginationOptions } from './Pagination';
import { Spin } from './Spin';

export type { Column, ColumnDef, Row } from '@tanstack/react-table';
export { createColumnHelper } from '@tanstack/react-table';

export interface RowSelectionOptions<TData> {
  selectedRows: TData[];
  onRowsSelect: (selectedRows: TData[]) => void;
  enableRowSelection?: (row: Row<TData>) => boolean;
}

export interface RowSortableOptions {
  onRowMove: (from: number, to: number) => void;
}

export interface RowStyleOptions<TData> {
  getRowStyle?: (row: Row<TData>) => ChakraV3.SystemStyleObject;
}

const DragHandleCell = <TData,>(props: { row: Row<TData> }) => {
  const { listeners, attributes } = DndKitSortable.useSortable({ id: props.row.id });
  return <DragHandle listeners={listeners} attributes={attributes} size='xs' />;
};

const CheckboxHeader = <TData,>(props: { table: RTable<TData> }) => {
  const { table } = props;
  const isAll = table.getIsAllPageRowsSelected();
  const isSome = table.getIsSomePageRowsSelected();
  const checked: boolean | 'indeterminate' = isAll ? true : isSome ? 'indeterminate' : false;

  return (
    <ChakraV3.Checkbox
      checked={checked}
      onCheckedChange={(details) => table.toggleAllPageRowsSelected(details.checked === true)}
      size='sm'
      colorPalette='purple'
      data-pw='tableCheckboxAll'
    />
  );
};

const CheckboxCell = <TData,>(props: { row: Row<TData> }) => {
  const { row } = props;
  return (
    <ChakraV3.Checkbox
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onCheckedChange={(details) => row.toggleSelected(details.checked === true)}
      size='sm'
      colorPalette='purple'
    />
  );
};

const SortableTr = <TData,>(props: {
  row: Row<TData>;
  isLastRow: boolean;
  rowStyleOptions?: RowStyleOptions<TData>;
}) => {
  const { row, isLastRow, rowStyleOptions } = props;

  const { setNodeRef, transform, transition } = DndKitSortable.useSortable({
    id: row.id,
  });

  const customStyle = rowStyleOptions?.getRowStyle?.(row);

  return (
    <ChakraV3.Table.Row
      ref={setNodeRef}
      display='flex'
      transform={CSS.Translate.toString(transform)}
      transition={transition}
      css={customStyle}
    >
      {row.getVisibleCells().map((cell) => (
        <ChakraV3.Table.Cell
          key={cell.id}
          display='flex'
          flex={`${cell.column.columnDef.size} 1 0%`}
          maxW={`${cell.column.columnDef.maxSize}px`}
          minW={`${cell.column.columnDef.minSize}px`}
          alignItems='center'
          whiteSpace='break-spaces'
          border={isLastRow ? 'none' : undefined}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </ChakraV3.Table.Cell>
      ))}
    </ChakraV3.Table.Row>
  );
};

interface Props<TData, TValue, TError> extends Omit<ChakraV3.Table.RootProps, 'columns'> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  error?: TError | null;
  pagination?: PaginationOptions | TimestampPaginationOptions;
  rowSelection?: RowSelectionOptions<TData>;
  rowSortable?: RowSortableOptions;
  rowStyle?: RowStyleOptions<TData>;
  getRowId?: (row: TData) => string;
  renderEmpty?: () => React.ReactElement;
  renderError?: (error: TError) => React.ReactElement;
}

export const DataTable = <TData, TValue, TError>(props: Props<TData, TValue, TError>) => {
  const {
    columns,
    data,
    isLoading,
    showHeader = true,
    showFooter = true,
    error,
    pagination,
    rowSortable,
    rowSelection,
    rowStyle,
    getRowId,
    renderEmpty,
    renderError,
    ...rest
  } = props;

  const rowSelectionPrime = React.useMemo(() => {
    if (!rowSelection) {
      return undefined;
    }

    const selectedRows = (rowSelection?.selectedRows ?? []).reduce<Record<number, boolean>>(
      (prev, curr) => {
        const index = data.indexOf(curr);
        prev[index] = true;
        return prev;
      },
      {},
    );

    const onRowsSelect = (value: (old: RowSelectionState) => RowSelectionState): void => {
      const selectedIndexes = Object.keys(value(selectedRows));
      const newSelectedRows = P.compact(
        selectedIndexes.map((index) => data.at(Number.parseInt(index, 10))),
      );
      rowSelection?.onRowsSelect(newSelectedRows);
    };

    return {
      selectedRows,
      onRowsSelect: onRowsSelect as OnChangeFn<RowSelectionState>,
      enableRowSelection: rowSelection?.enableRowSelection,
    };
  }, [data, rowSelection]);

  const isRowSelectionEnabled = React.useMemo(() => {
    return !P.isNil(rowSelection);
  }, [rowSelection]);

  const isRowSortableEnabled = React.useMemo(() => {
    return !P.isNil(rowSortable);
  }, [rowSortable]);

  const cs: ColumnDef<TData, TValue>[] = React.useMemo(() => {
    const allColumns: ColumnDef<TData, TValue>[] = [];

    if (isRowSelectionEnabled) {
      allColumns.push({
        id: 'select',
        header: ({ table }) => <CheckboxHeader table={table} />,
        cell: ({ row }) => <CheckboxCell row={row} />,
        size: 32,
        minSize: 32,
        maxSize: 32,
        enableSorting: false,
        enableHiding: false,
      });
    }

    if (isRowSortableEnabled) {
      allColumns.push({
        id: 'drag',
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: DragHandleCell,
      });
    }

    allColumns.push(...columns);

    return allColumns;
  }, [isRowSelectionEnabled, isRowSortableEnabled, columns]);

  const table = useReactTable({
    data,
    columns: cs,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: rowSelectionPrime?.onRowsSelect,
    enableRowSelection: rowSelectionPrime?.enableRowSelection,
    state: {
      rowSelection: rowSelectionPrime?.selectedRows,
    },
    defaultColumn: {
      size: 150,
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  const sensors = DndKitCore.useSensors(
    DndKitCore.useSensor(DndKitCore.PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const rows = table.getRowModel().rows;
  const { setRowSelection } = table;

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to run this effect only when data changes
  React.useEffect(() => {
    setRowSelection(() => ({}));
  }, [data, setRowSelection]);

  const onDragEnd = React.useCallback(
    (event: DndKitCore.DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = rows.findIndex((r) => r.id === active.id);
        const newIndex = rows.findIndex((r) => r.id === over.id);
        rowSortable?.onRowMove(oldIndex, newIndex);
      }
    },
    [rowSortable, rows],
  );

  const tableHeaderContent = (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <ChakraV3.Table.Row key={headerGroup.id} display='flex'>
          {headerGroup.headers.map((header) => {
            return (
              <ChakraV3.Table.ColumnHeader
                key={header.id}
                display='flex'
                flex={`${header.column.columnDef.size} 1 0%`}
                maxW={`${header.column.columnDef.maxSize}px`}
                minW={`${header.column.columnDef.minSize}px`}
                alignItems='center'
                py={2}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </ChakraV3.Table.ColumnHeader>
            );
          })}
        </ChakraV3.Table.Row>
      ))}
    </>
  );

  const tableBodyContent = P.run(() => {
    if (isLoading) {
      return (
        <ChakraV3.Table.Row>
          <ChakraV3.Table.Cell>
            <ChakraV3.Center py={8}>
              <Spin />
            </ChakraV3.Center>
          </ChakraV3.Table.Cell>
        </ChakraV3.Table.Row>
      );
    }

    if (table.getRowModel().rows.length === 0) {
      return (
        <ChakraV3.Table.Row>
          <ChakraV3.Table.Cell>{renderEmpty ? renderEmpty() : <Empty />}</ChakraV3.Table.Cell>
        </ChakraV3.Table.Row>
      );
    }

    if (error) {
      return (
        <ChakraV3.Table.Row>
          <ChakraV3.Table.Cell>{renderError ? renderError(error) : <Empty />}</ChakraV3.Table.Cell>
        </ChakraV3.Table.Row>
      );
    }

    return (
      <DndKitCore.DndContext
        accessibility={{ container: document.body }}
        sensors={sensors}
        collisionDetection={DndKitCore.closestCenter}
        onDragEnd={onDragEnd}
      >
        <DndKitSortable.SortableContext
          strategy={DndKitSortable.verticalListSortingStrategy}
          items={rows.map((row) => row.id)}
        >
          {rows.map((row) => (
            <SortableTr
              key={row.id}
              row={row}
              isLastRow={row.index === rows.length - 1}
              rowStyleOptions={rowStyle}
            />
          ))}
        </DndKitSortable.SortableContext>
      </DndKitCore.DndContext>
    );
  });

  const tableCaptionContent = P.run(() => {
    if (isLoading || error) {
      return null;
    }

    return (
      <ChakraV3.HStack>
        {rowSelectionPrime && (
          <ChakraV3.Text color='secondary'>
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </ChakraV3.Text>
        )}
        <ChakraV3.Spacer />
        {pagination && <Pagination {...pagination} />}
      </ChakraV3.HStack>
    );
  });

  return (
    <ChakraV3.Table.ScrollArea w='full'>
      <ChakraV3.Table.Root {...rest}>
        {showHeader && <ChakraV3.Table.Header>{tableHeaderContent}</ChakraV3.Table.Header>}
        <ChakraV3.Table.Body>{tableBodyContent}</ChakraV3.Table.Body>
        {showFooter && <ChakraV3.Table.Caption>{tableCaptionContent}</ChakraV3.Table.Caption>}
      </ChakraV3.Table.Root>
    </ChakraV3.Table.ScrollArea>
  );
};
